import React, { useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";
import { Chart, ArcElement, Tooltip, Legend } from "chart.js";
import "./AdminDashboard.scss";

Chart.register(ArcElement, Tooltip, Legend);

interface Grievance {
  id: number;
  subject: string;
  description: string;
  status: "Pending" | "In Progress" | "Resolved";
  assigned_to?: string;
}

interface TeamMember {
  id: number;
  name: string;
  email: string;
  password: string;
}

const AdminDashboard: React.FC = () => {
  const [grievances, setGrievances] = useState<Grievance[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<TeamMember>({ id: 0, name: "", email: "", password: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const grievancesPerPage = 5;

  useEffect(() => {
    fetchGrievances();
    fetchTeamMembers();
  }, []);

  const fetchGrievances = async () => {
    try {
      const response = await fetch("http://sjmpolytechnicbirur.in/api/get_grievances.php");
      const data = await response.json();
  
      // Check if it's an array
      if (Array.isArray(data.grievances)) {
        setGrievances(data.grievances);
      } else {
        console.error("Invalid grievance data format", data);
        setGrievances([]); // fallback to empty array
      }
    } catch (error) {
      console.error("Error fetching grievances:", error);
      setGrievances([]); // fallback on error
    }
  };
  

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch("http://sjmpolytechnicbirur.in/api/get_team_members.php");
      const data = await response.json();
      // Check if it's an array
      if (Array.isArray(data.team_members)) {
        setTeamMembers(data.team_members);
      } else {
        console.error("Invalid grievance data format", data);
        setTeamMembers([]); // fallback to empty array
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };

  const assignGrievance = async (grievanceId: number, assignedToName: string) => {
    const selectedMember = teamMembers.find((member) => member.name === assignedToName);
    if (!selectedMember) return;

    try {
      const response = await fetch("http://sjmpolytechnicbirur.in/api/assign_grievance.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          grievance_id: grievanceId,
          assigned_to: selectedMember.name,
          assigned_to_email: selectedMember.email,
        }),
      });

      if (response.ok) {
        alert(`Grievance assigned to ${selectedMember.name} and email sent.`);
        fetchGrievances();
      } else {
        console.error("Failed to assign grievance.");
      }
    } catch (error) {
      console.error("Error assigning grievance:", error);
    }
  };

  const grievanceStats = grievances.reduce(
    (acc, g) => {
      acc[g.status]++;
      return acc;
    },
    { Pending: 0, "In Progress": 0, Resolved: 0 }
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addOrEditTeamMember = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      alert("All fields are required.");
      return;
    }

    try {
      const response = await fetch("http://sjmpolytechnicbirur.in/api/add_team_member.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: isEditing ? formData.id : undefined,
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: "Lecturer",
        }),
      });

      const data = await response.json();
      if (data.success) {
        alert(data.message);
        fetchTeamMembers();
        setFormData({ id: 0, name: "", email: "", password: "" });
        setShowForm(false);
        setIsEditing(false);
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Error adding team member:", error);
    }
  };

  const editTeamMember = (member: TeamMember) => {
    setFormData(member);
    setShowForm(true);
    setIsEditing(true);
  };

  const deleteTeamMember = (id: number) => {
    setTeamMembers(teamMembers.filter((member) => member.id !== id));
  };

  const handleLogout = async () => {
    try {
      await fetch("http://sjmpolytechnicbirur.in/api/logout.php", { credentials: "include" });
      localStorage.removeItem("user");
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const filteredGrievances = grievances.filter((g) =>
    g.subject.toLowerCase().includes(search.toLowerCase())
  );

  const indexOfLastGrievance = currentPage * grievancesPerPage;
  const indexOfFirstGrievance = indexOfLastGrievance - grievancesPerPage;
  const currentGrievances = filteredGrievances.slice(indexOfFirstGrievance, indexOfLastGrievance);

  return (
    <div className="admin-dashboard">
      <h1>Admin Dashboard</h1>
      <button className="logout-btn" onClick={handleLogout}>Logout</button>

      <div className="top-bar">
        <input
          type="text"
          placeholder="Search grievances..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button onClick={() => setShowForm(true)}>+ Add Team Member</button>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{isEditing ? "Edit Lecturer" : "Register Lecturer"}</h2>
            <input type="text" name="name" placeholder="Full Name" value={formData.name} onChange={handleInputChange} />
            <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleInputChange} />
            <input type="password" name="password" placeholder="Password" value={formData.password} onChange={handleInputChange} />
            <button onClick={addOrEditTeamMember}>{isEditing ? "Update" : "Register"}</button>
            <button className="cancel-btn" onClick={() => { setShowForm(false); setIsEditing(false); }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="chart-container">
        <Pie
          data={{
            labels: ["Pending", "In Progress", "Resolved"],
            datasets: [
              {
                data: [grievanceStats.Pending, grievanceStats["In Progress"], grievanceStats.Resolved],
                backgroundColor: ["#ff4d4d", "#ffcc00", "#4caf50"],
              },
            ],
          }}
        />
      </div>

      <div className="grievance-list">
        <h2>Grievance list</h2>
        {currentGrievances.map((g) => (
          <div key={g.id} className="grievance-card">
            <h3>{g.subject}</h3>
            <p>{g.description}</p>
            <p>
              Status: <span className={`status ${g.status.toLowerCase()}`}>{g.status}</span>
            </p>
            <p>Assigned To: {g.assigned_to || "Not Assigned"}</p>
            <select
              onChange={(e) => assignGrievance(g.id, e.target.value)}
              value={g.assigned_to || ""}
            >
              <option value="">Select Lecturer</option>
              {teamMembers.map((member) => (
                <option key={member.id} value={member.name}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div className="pagination">
        {Array.from({ length: Math.ceil(filteredGrievances.length / grievancesPerPage) }, (_, i) => (
          <button key={i} onClick={() => setCurrentPage(i + 1)} className={currentPage === i + 1 ? "active" : ""}>
            {i + 1}
          </button>
        ))}
      </div>

      <h2>Team Members</h2>
      <table className="team-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {teamMembers.map((member) => (
            <tr key={member.id}>
              <td>{member.name}</td>
              <td>{member.email}</td>
              <td>
                <button className="edit-btn" onClick={() => editTeamMember(member)}>Edit</button>
                <button className="delete-btn" onClick={() => deleteTeamMember(member.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminDashboard;
